/**
 * What: Displays saved working drafts and prepared tests in one searchable, filterable Polaris resource index.
 * Transport / data-flow: Receives setup history and resumable draft summaries.
 *
 */
import {
  PreparedSetupsCollectionEmpty,
  PreparedSetupsDraftsNotice,
} from './PreparedSetupsIndexStates';
import { studySetupsIndexDefinition, studySetupRenderers } from './studySetupsIndex';
import { FilteredEmpty } from './FilteredEmpty';
import {
  ShopifyResourceIndex,
  type ShopifyResourceIndexAppliedFilter,
  type ShopifyResourceIndexFacet,
} from './ShopifyResourceIndex';
import type { IndexView } from './resource-index/indexSchema';
import { shopifyComponentCopy, shopifyIndexCopy } from '@testfeed/product-language';
import { ShopifyPageSkeleton } from './ShopifyPageSkeleton';
import { ShopifyResourceIndexSurface } from './ShopifyResourceIndexSurface';
import type { StudySetupIndexRow } from './study-setups-page-controller-types'
import type { ReactNode } from 'react'

export type PreparedSetupsFilterGroup = ShopifyResourceIndexFacet;
export type PreparedSetupsAppliedFilter = ShopifyResourceIndexAppliedFilter;


type PreparedSetupsPanelProps = {
  rows: StudySetupIndexRow[];
  onResumeDraft?: (draftId: string, studyId?: string, status?: string, runId?: string | null) => void;
  renderDraftActions?: (draft: StudySetupIndexRow['draft']) => ReactNode;
  isLoading?: boolean;
  onCreateStudy?: () => void;
  onClearFilters?: () => void;
  hasAnySetups?: boolean;
  /** Whether any draft or test actually loaded. When some did, an empty table means the filters matched nothing. */
  hasLoadedRows?: boolean;
  views?: IndexView[];
  selectedView?: string;
  onSelectView?: (id: string) => void;
  queryValue?: string;
  onQueryChange?: (value: string) => void;
  sortSelected?: string[];
  onSort?: (value: string[]) => void;
  filters?: PreparedSetupsFilterGroup[];
  appliedFilters?: PreparedSetupsAppliedFilter[];
  draftsError?: unknown;
  onRetryDrafts?: () => void;
  page?: number;
  pages?: number;
  onPageChange?: (page: number) => void;
  selection?: { selectedIds: string[]; onChange: (ids: string[]) => void; getSelectable?: (row: StudySetupIndexRow) => boolean; getLabel?: (row: StudySetupIndexRow) => string };
  onDeleteSelected?: () => void;
};

/** Shows saved builder drafts and prepared test setups in one resource index. */
export function PreparedSetupsPanel({
  rows,
  onResumeDraft,
  renderDraftActions,
  isLoading = false,
  onCreateStudy,
  onClearFilters,
  hasAnySetups = true,
  hasLoadedRows = true,
  views = [],
  selectedView,
  onSelectView = () => undefined,
  queryValue = '',
  onQueryChange = () => undefined,
  sortSelected = ['created desc'],
  onSort = () => undefined,
  filters = [],
  appliedFilters = [],
  draftsError = null,
  onRetryDrafts,
  page = 1,
  pages = 1,
  onPageChange,
  selection,
  onDeleteSelected,
}: PreparedSetupsPanelProps) {
  const draftsNotice = <PreparedSetupsDraftsNotice draftsError={draftsError} onRetry={onRetryDrafts} />;
  if (rows.length === 0 && !hasAnySetups && isLoading) {
    return <ShopifyPageSkeleton layout="index" accessibilityLabel={shopifyIndexCopy.loading} />;
  }
  // Only when nothing loaded: with loaded rows, an empty table is a filter result and must keep its filters.
  if (rows.length === 0 && !isLoading && !hasLoadedRows && draftsError) {
    return (
      <s-stack direction="block" gap="base">
        {draftsNotice}
      </s-stack>
    );
  }

  if (rows.length === 0 && !hasAnySetups) {
    return (
      <PreparedSetupsCollectionEmpty onCreateStudy={onCreateStudy} />
    );
  }

  return (
    <ShopifyResourceIndexSurface notice={draftsNotice}>
      <ShopifyResourceIndex
        rows={rows}
        renderers={studySetupRenderers}
        definition={studySetupsIndexDefinition}
        selection={selection}
        onDeleteSelected={onDeleteSelected}
        isLoading={isLoading}
        getRowId={(row) => row.id}
        getRowAction={(model) => model.handleOpenStudy}
        prepareRow={(row, render) => render({
          kind: 'builder-draft',
          draft: row.draft,
          handleOpenStudy: onResumeDraft ? () => onResumeDraft(row.draft.draftId, row.draft.studyId, row.draft.state?.status ?? (row.draft.deletion ? 'deleting' : undefined), row.draft.state?.latestRunId) : undefined,
          rowLinkId: `saved-study-builder-draft-${row.draft.draftId}`,
          actions: renderDraftActions?.(row.draft),
        })}
        views={views}
        selectedView={selectedView}
        onSelectView={onSelectView}
        sortSelected={sortSelected}
        onSort={onSort}
        queryValue={queryValue}
        onQueryChange={onQueryChange}
        facets={filters}
        appliedFilters={appliedFilters}
        onClearFilters={onClearFilters}
        pagination={pages > 1 && onPageChange ? {
          label: shopifyIndexCopy.pageLabel(page, pages),
          hasPrevious: page > 1,
          hasNext: page < pages,
          onPrevious: () => onPageChange(page - 1),
          onNext: () => onPageChange(page + 1),
        } : undefined}
        filteredEmptyState={
          <FilteredEmpty
            isLoading={isLoading}
            refreshingBody={shopifyComponentCopy.preparedSetupsPanel.refreshingPreparedSetups001}
            title={shopifyComponentCopy.preparedSetupsPanel.preparedStudySetups002}
            body={shopifyComponentCopy.preparedSetupsPanel.noSetupsMatchTheSelected006}
            clearFiltersLabel={shopifyComponentCopy.preparedSetupsPanel.clearFilters007}
            onClearFilters={onClearFilters}
          />
        } />
    </ShopifyResourceIndexSurface>
  );
}
